export interface ApprovedSpecializationCatalogEntry {
  id?: string;
  name: string;
  category: string;
}

export interface ApprovedSpecializationCatalogIndex {
  entries: ApprovedSpecializationCatalogEntry[];
  nameByKey: Map<string, string>;
  categoryByNameKey: Map<string, string>;
  categoryByKey: Map<string, string>;
}

const normalizeCatalogLabel = (value: unknown): string =>
  String(value || '')
    .trim()
    .replace(/\s+/g, ' ');

export const normalizeSpecializationCatalogKey = (value: unknown): string =>
  normalizeCatalogLabel(value).toLocaleLowerCase('pl-PL');

const sortLocale = (values: string[]): string[] =>
  [...values].sort((a, b) => a.localeCompare(b, 'pl'));

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => normalizeCatalogLabel(item)).filter(Boolean);
};

export const buildApprovedSpecializationCatalogIndex = (
  entries: ApprovedSpecializationCatalogEntry[]
): ApprovedSpecializationCatalogIndex => {
  const dedupeEntryKeys = new Set<string>();
  const normalizedEntries: ApprovedSpecializationCatalogEntry[] = [];
  const nameByKey = new Map<string, string>();
  const categoryByNameKey = new Map<string, string>();
  const categoryByKey = new Map<string, string>();

  entries.forEach((entry) => {
    const name = normalizeCatalogLabel(entry.name);
    const category = normalizeCatalogLabel(entry.category);
    if (!name || !category) return;

    const nameKey = normalizeSpecializationCatalogKey(name);
    const categoryKey = normalizeSpecializationCatalogKey(category);
    const dedupeKey = `${nameKey}::${categoryKey}`;
    if (dedupeEntryKeys.has(dedupeKey)) return;
    dedupeEntryKeys.add(dedupeKey);

    normalizedEntries.push({
      id: entry.id,
      name,
      category,
    });

    if (!nameByKey.has(nameKey)) {
      nameByKey.set(nameKey, name);
      categoryByNameKey.set(nameKey, category);
    }
    if (!categoryByKey.has(categoryKey)) {
      categoryByKey.set(categoryKey, category);
    }
  });

  normalizedEntries.sort((a, b) => {
    const byName = a.name.localeCompare(b.name, 'pl');
    if (byName !== 0) return byName;
    return a.category.localeCompare(b.category, 'pl');
  });

  return {
    entries: normalizedEntries,
    nameByKey,
    categoryByNameKey,
    categoryByKey,
  };
};

export const normalizeProviderSpecializationFields = (
  provider: {
    specializations?: unknown;
    offeredCategories?: unknown;
  },
  catalog: ApprovedSpecializationCatalogIndex
): {
  specializations: string[];
  offeredCategories: string[];
} => {
  const rawSpecializations = toStringArray(provider.specializations);
  const rawOfferedCategories = toStringArray(provider.offeredCategories);

  const specializationSet = new Set<string>();
  rawSpecializations.forEach((value) => {
    const key = normalizeSpecializationCatalogKey(value);
    const approvedName = catalog.nameByKey.get(key);
    if (approvedName) specializationSet.add(approvedName);
  });

  const categorySet = new Set<string>();
  specializationSet.forEach((specialization) => {
    const specializationKey = normalizeSpecializationCatalogKey(specialization);
    const approvedCategory = catalog.categoryByNameKey.get(specializationKey);
    if (approvedCategory) categorySet.add(approvedCategory);
  });

  rawOfferedCategories.forEach((value) => {
    const key = normalizeSpecializationCatalogKey(value);
    const approvedCategory = catalog.categoryByKey.get(key);
    if (approvedCategory) categorySet.add(approvedCategory);
  });

  return {
    specializations: sortLocale(Array.from(specializationSet)),
    offeredCategories: sortLocale(Array.from(categorySet)),
  };
};
